import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { CalendarClock, AlertTriangle, Inbox, RefreshCw, List, Route, ClipboardList } from 'lucide-react';
import { serviceCallsAPI } from '@shared/apiClient';
import { formatDate } from '@shared/utils';
import ServiceCallModal from '../components/ServiceCallModal';
import DailySuppliesModal from '../components/DailySuppliesModal';
import { Section, CallCard } from '../components/AssignmentListItems';
import RouteMap from '../components/RouteMap';

const toYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Keep Today's list in sync with the field without requiring a manual reload,
// mirroring the polling cadence used on Dashboard.
const REFRESH_INTERVAL_MS = 30000;

export default function Today() {
  const queryClient = useQueryClient();
  const [selectedCall, setSelectedCall] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [showSuppliesModal, setShowSuppliesModal] = useState(false);

  const todayStr = useMemo(() => toYmd(new Date()), []);

  const todayQuery = useQuery(
    ['todayDaily', todayStr],
    () => serviceCallsAPI.getDailyClients(todayStr),
    { refetchInterval: REFRESH_INTERVAL_MS, refetchOnWindowFocus: true }
  );

  const overdueQuery = useQuery(
    ['todayOverdue'],
    () => serviceCallsAPI.getOverdueScheduled(),
    { refetchInterval: REFRESH_INTERVAL_MS, refetchOnWindowFocus: true }
  );

  const unscheduledQuery = useQuery(
    ['todayUnscheduled'],
    () => serviceCallsAPI.getUnscheduledServiceCalls(),
    { refetchInterval: REFRESH_INTERVAL_MS, refetchOnWindowFocus: true }
  );

  const todayCalls = todayQuery.data?.data?.dailyClients || [];
  const overdueCalls = overdueQuery.data?.data?.serviceCalls || [];
  const unscheduledCalls = unscheduledQuery.data?.data?.serviceCalls || [];

  const refreshToday = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries('todayDaily'),
        queryClient.invalidateQueries('todayOverdue'),
        queryClient.invalidateQueries('todayUnscheduled')
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedCall(null);
    refreshToday();
  };

  const isLoading =
    todayQuery.isLoading || overdueQuery.isLoading || unscheduledQuery.isLoading;

  const totalOpen = todayCalls.length + overdueCalls.length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <CalendarClock className="h-6 w-6 text-primary-600" />
            <h1 className="text-2xl font-bold text-slate-900">Today</h1>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {formatDate(new Date(), 'EEEE, MMMM d, yyyy')}
            {!isLoading && (
              <span className="ml-2 text-slate-400">
                · {totalOpen} {totalOpen === 1 ? 'call' : 'calls'} to run
              </span>
            )}
          </p>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowSuppliesModal(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50"
          >
            <ClipboardList className="h-3.5 w-3.5" />
            Parts List
          </button>
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                viewMode === 'list' ? 'bg-primary-600 text-white' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <List className="h-3.5 w-3.5" />
              List
            </button>
            <button
              type="button"
              onClick={() => setViewMode('route')}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                viewMode === 'route' ? 'bg-primary-600 text-white' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Route className="h-3.5 w-3.5" />
              Route
            </button>
          </div>
          <button
            type="button"
            onClick={refreshToday}
            disabled={isLoading || isRefreshing}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {viewMode === 'route' ? (
        <RouteMap date={todayStr} />
      ) : isLoading ? (
        <p className="text-sm text-slate-400">Loading your day…</p>
      ) : (
        <>
          <Section
            title="Today"
            icon={CalendarClock}
            count={todayCalls.length}
            emptyText="No service calls scheduled for today."
          >
            {todayCalls.map((call) => (
              <CallCard key={call.id} call={call} onClick={() => setSelectedCall(call)} />
            ))}
          </Section>

          {overdueCalls.length > 0 && (
            <Section
              title="Overdue"
              icon={AlertTriangle}
              count={overdueCalls.length}
              tone="danger"
            >
              {overdueCalls.map((call) => (
                <CallCard
                  key={call.id}
                  call={call}
                  tone="danger"
                  subtitle={call.scheduledDate ? `Scheduled ${formatDate(call.scheduledDate)}` : null}
                  onClick={() => setSelectedCall(call)}
                />
              ))}
            </Section>
          )}

          <Section
            title="Unscheduled"
            icon={Inbox}
            count={unscheduledCalls.length}
            emptyText="Nothing waiting to be scheduled."
          >
            {unscheduledCalls.map((call) => (
              <CallCard key={call.id} call={call} onClick={() => setSelectedCall(call)} />
            ))}
          </Section>
        </>
      )}

      {selectedCall && (
        <ServiceCallModal
          serviceCall={selectedCall}
          isOpen={!!selectedCall}
          onClose={handleCloseModal}
          isRepairCall={!!selectedCall.isPriority}
        />
      )}

      <DailySuppliesModal
        date={todayStr}
        isOpen={showSuppliesModal}
        onClose={() => setShowSuppliesModal(false)}
      />
    </div>
  );
}
